import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RefreshCw } from "lucide-react";
import { getAuditLogs, type AuditLogEntry } from "@/lib/api";
import { AuditLogOutcomeBadge } from "@/components/AuditLogOutcomeBadge";

export const Route = createFileRoute("/dashboard/audit-logs/")({
  component: AuditLogsPage,
});

function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const [actorUid, setActorUid] = useState("");
  const [urlPrefix, setUrlPrefix] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [limit, setLimit] = useState<number | "">(50);

  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAuditLogs({
        actorUid: actorUid.trim() || undefined,
        url: urlPrefix.trim() || undefined,
        from: fromDate ? new Date(fromDate).toISOString() : undefined,
        to: toDate ? new Date(toDate).toISOString() : undefined,
        limit: limit || 50,
      });
      setLogs(result.logs);
      setNextCursor(result.nextCursor);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load audit logs"
      );
    } finally {
      setLoading(false);
    }
  }, [actorUid, urlPrefix, fromDate, toDate, limit]);

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    setError(null);
    try {
      const result = await getAuditLogs({
        actorUid: actorUid.trim() || undefined,
        url: urlPrefix.trim() || undefined,
        from: fromDate ? new Date(fromDate).toISOString() : undefined,
        to: toDate ? new Date(toDate).toISOString() : undefined,
        limit: limit || 50,
        cursor: nextCursor,
      });
      setLogs((prev) => [...prev, ...result.logs]);
      setNextCursor(result.nextCursor);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load more audit logs"
      );
    } finally {
      setLoadingMore(false);
    }
  }, [actorUid, urlPrefix, fromDate, toDate, limit, nextCursor]);

  useEffect(() => {
    loadFirstPage();
    // Only run on mount - filter changes apply via the "Apply Filters" button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Audit Log</h1>
            <p className="text-lg text-muted-foreground">
              Read-only view of admin actions recorded in the platform audit
              trail.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadFirstPage}
            disabled={loading}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
            <CardDescription>
              Filter by actor UID, URL prefix, and date range.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="actorUid">Actor UID</Label>
                <Input
                  id="actorUid"
                  placeholder="uid..."
                  value={actorUid}
                  onChange={(e) => setActorUid(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="urlPrefix">URL prefix</Label>
                <Input
                  id="urlPrefix"
                  placeholder="/api/clients"
                  value={urlPrefix}
                  onChange={(e) => setUrlPrefix(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fromDate">From</Label>
                <Input
                  id="fromDate"
                  type="datetime-local"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="toDate">To</Label>
                <Input
                  id="toDate"
                  type="datetime-local"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-end gap-4">
              <div className="space-y-2">
                <Label htmlFor="limit">Page size</Label>
                <Input
                  id="limit"
                  type="number"
                  min="1"
                  max="200"
                  className="w-24"
                  value={limit}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "") {
                      setLimit("");
                    } else {
                      const parsed = parseInt(val, 10);
                      if (!isNaN(parsed)) {
                        setLimit(Math.min(200, Math.max(1, parsed)));
                      }
                    }
                  }}
                />
              </div>
              <Button onClick={loadFirstPage} disabled={loading}>
                Apply Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Entries</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading audit logs...
              </div>
            ) : logs.length === 0 && !nextCursor ? (
              <div className="text-center py-8 text-muted-foreground">
                No audit log entries found
              </div>
            ) : (
              <div className="space-y-2">
                <div className="hidden md:grid grid-cols-[160px_1fr_1fr_80px_100px] gap-4 px-4 text-xs font-medium text-muted-foreground uppercase">
                  <div>Timestamp</div>
                  <div>Actor</div>
                  <div>Method &amp; URL</div>
                  <div>Status</div>
                  <div>Outcome</div>
                </div>
                {logs.map((entry) => (
                  <div
                    key={entry.id}
                    className="border rounded-lg p-4 grid grid-cols-1 md:grid-cols-[160px_1fr_1fr_80px_100px] gap-2 md:gap-4 md:items-center"
                  >
                    <div className="text-xs text-muted-foreground">
                      {entry.createdAt
                        ? new Date(entry.createdAt).toLocaleString()
                        : "—"}
                    </div>
                    <div
                      className="text-sm truncate"
                      title={entry.actorUid ?? undefined}
                    >
                      {entry.actorEmail || entry.actorUid || "—"}
                    </div>
                    <div className="text-sm font-mono truncate">
                      {entry.method
                        ? `${entry.method} ${entry.url}`
                        : entry.action || "—"}
                    </div>
                    <div className="text-sm">{entry.statusCode ?? "—"}</div>
                    <div>
                      <AuditLogOutcomeBadge entry={entry} />
                    </div>
                    {(entry.detail || entry.targetUserId) && (
                      <div className="md:col-span-5 text-xs text-muted-foreground">
                        {entry.targetUserId && (
                          <span>Target: {entry.targetUserId}. </span>
                        )}
                        {entry.detail && <span>{entry.detail}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {nextCursor && !loading && (
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading..." : "Load more"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
